import React, { useEffect, useState } from "react";
import API from "../../api/axiosConfig";
import {
  DragDropContext,
  Droppable,
  Draggable
} from "@hello-pangea/dnd";
import socket from "../../socket";
import Swal from "sweetalert2";
import {
  Tag,
  Clock,
  CalendarDays,
  BarChart3
} from "lucide-react";

const sonido = new Audio("/notificacion.mp3");

const estados = ["En espera", "En proceso", "Culminado"];

const Kanban = () => {
  const [tickets, setTickets] = useState([]);

  // ======================================
  // 🔹 OBTENER TICKETS
  // ======================================
  const obtenerTickets = async () => {
    try {
      const res = await API.get("/tickets");
      setTickets(res.data);
    } catch (error) {
      console.error("Error cargando tickets", error);
    }
  };

  // ======================================
  // 🔥 SOCKET + NOTIFICACIONES
  // ======================================
  useEffect(() => {
    obtenerTickets();

    const handleNuevoTicket = (data) => {
      Swal.fire({
        icon: "info",
        title: "Nuevo ticket",
        text: data.descripcion,
        timer: 3000,
        showConfirmButton: false
      });

      // 🔊 sonido (manejo seguro)
      sonido.currentTime = 0;
      sonido.play().catch(() => {
        console.log("🔇 Sonido bloqueado");
      });

      obtenerTickets();
    };

    const handleActualizar = () => {
      obtenerTickets();
    };

    socket.on("nuevo_ticket", handleNuevoTicket);
    socket.on("actualizar_ticket", handleActualizar);

    return () => {
      socket.off("nuevo_ticket", handleNuevoTicket);
      socket.off("actualizar_ticket", handleActualizar);
    };
  }, []);

  // ======================================
  // 🔹 AGRUPAR POR ESTADO
  // ======================================
  const columnas = estados.reduce((acc, estado) => {
    acc[estado] = tickets.filter((t) => t.estado === estado);
    return acc;
  }, {});

  // ======================================
  // 🔥 DRAG & DROP
  // ======================================
  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (source.droppableId === destination.droppableId) return;

    try {
      await API.put(`/tickets/${draggableId}`, {
        estado: destination.droppableId
      });

      obtenerTickets();
    } catch (error) {
      console.error("Error moviendo ticket", error);
    }
  };

  // ======================================
  // 🎨 UI
  // ======================================
  return (
    <div className="p-4 bg-gray-900 min-h-screen text-white">

      {/* HEADER */}
      <h1 className="text-2xl font-bold text-center mb-4">
        📊 Panel de Incidencias ({tickets.length})
      </h1>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex flex-col md:flex-row gap-4">

          {estados.map((estado) => (
            <Droppable droppableId={estado} key={estado}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="bg-gray-800 rounded-xl p-3 min-h-[400px] w-full md:w-1/3 shadow-lg"
                >

                  {/* TITULO */}
                  <h2 className="text-lg font-semibold text-center mb-3">
                    {estado}
                  </h2>

                  {/* TARJETAS */}
                  {columnas[estado].map((ticket, index) => (
                    <Draggable
                      key={ticket.id.toString()}
                      draggableId={ticket.id.toString()}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{ ...provided.draggableProps.style }}
                          className="bg-gray-700 p-3 rounded-xl mb-3 shadow-lg hover:scale-105 transition duration-200 cursor-pointer"
                        >

                          {/* IMAGEN */}
                          {ticket.imagen && (
                            <img
                              src={ticket.imagen}
                              alt=""
                              className="w-full h-32 object-cover rounded-lg mb-2"
                            />
                          )}

                          {/* DESCRIPCIÓN */}
                          <p className="text-sm mb-2 font-medium">
                            {ticket.descripcion}
                          </p>

                          {/* INFO HORIZONTAL PRO */}
                          <div className="flex flex-wrap items-center gap-4 text-xs mt-2 border-t border-gray-600 pt-2">

                            {/* AREA */}
                            <div className="flex items-center gap-1 text-gray-300">
                              <Tag size={14} className="text-blue-400" />
                              <span className="font-semibold">Área:</span>
                              <span className="bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded-full">
                                {ticket.area}
                              </span>
                            </div>

                            {/* ESTADO */}
                            <div className="flex items-center gap-1">
                              <BarChart3
                                size={14}
                                className={
                                  ticket.estado === "En espera"
                                    ? "text-yellow-400"
                                    : ticket.estado === "En proceso"
                                      ? "text-blue-400"
                                      : "text-green-400"
                                }
                              />
                              <span className="font-semibold text-gray-300">Estado:</span>
                              <span
                                className={`font-semibold ${ticket.estado === "En espera"
                                    ? "text-yellow-400"
                                    : ticket.estado === "En proceso"
                                      ? "text-blue-400"
                                      : "text-green-400"
                                  }`}
                              >
                                {ticket.estado}
                              </span>
                            </div>

                            {/* FECHA */}
                            <div className="flex items-center gap-1 text-gray-300">
                              <CalendarDays size={14} className="text-purple-400" />
                              <span className="font-semibold">
                                {new Date(ticket.fecha).toLocaleDateString()}
                              </span>
                            </div>

                            {/* HORA */}
                            <div className="flex items-center gap-1 text-gray-300">
                              <Clock size={14} className="text-pink-400" />
                              <span className="font-semibold">
                                {new Date(ticket.fecha).toLocaleTimeString()}
                              </span>
                            </div>

                          </div>

                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}

                </div>
              )}
            </Droppable>
          ))}

        </div>
      </DragDropContext>

    </div>
  );
};

export default Kanban;